/**
 * Google Calendar API service.
 * Uses Google Workspace MCP Gateway for data fetching.
 * Interfaces remain unchanged for panel compatibility.
 */
import { mcpCallOrThrow, isMcpConnected } from './googleMcp'
const CALENDAR_API = 'https://www.googleapis.com/calendar/v3'
export interface CalendarEvent {
  id: string
  summary: string
  description?: string
  start: {
    dateTime?: string
    date?: string
    timeZone?: string
  }
  end: {
    dateTime?: string
    date?: string
    timeZone?: string
  }
  location?: string
  status: string
  htmlLink: string
  creator?: {
    email: string
    displayName?: string
  }
  organizer?: {
    email: string
    displayName?: string
  }
  attendees?: Array<{
    email: string
    displayName?: string
    responseStatus: string
    self?: boolean
  }>
  hangoutLink?: string
  conferenceData?: {
    entryPoints?: Array<{
      entryPointType: string
      uri: string
    }>
  }
  colorId?: string
  recurringEventId?: string
}
interface CalendarListResponse {
  items: CalendarEvent[]
  nextPageToken?: string
  summary?: string
  timeZone?: string
}
export const googleCalendar = {
  /**
   * Get events from the primary calendar.
   */
  async getEvents(
    timeMin?: string,
    timeMax?: string,
    maxResults: number = 50,
  ): Promise<CalendarEvent[]> {
    if (!isMcpConnected())
      throw new Error(
        'Google no está conectado. Conectá tu cuenta desde Conectores.',
      )
    const now = new Date()
    const startOfToday = new Date(
      now.getFullYear(),
      now.getMonth(),
      now.getDate(),
    )
    const weekFromNow = new Date(
      startOfToday.getTime() + 7 * 24 * 60 * 60 * 1000,
    )
    const result = await mcpCallOrThrow<any>('calendar_list_events', {
      calendar_id: 'primary',
      time_min: timeMin || startOfToday.toISOString(),
      time_max: timeMax || weekFromNow.toISOString(),
      max_results: maxResults,
      query: '',
    })

    // MCP may return data in different shapes — normalize to array
    console.log(
      '[GoogleCalendar] Raw MCP response type:',
      typeof result,
      Array.isArray(result) ? 'array' : 'object',
    )
    if (Array.isArray(result)) return result
    if (result?.items && Array.isArray(result.items)) return result.items
    if (result?.events && Array.isArray(result.events)) return result.events
    if (result?.data && Array.isArray(result.data)) return result.data
    if (result?.data?.items && Array.isArray(result.data.items))
      return result.data.items
    console.warn(
      '[GoogleCalendar] Unexpected response shape, keys:',
      result ? Object.keys(result) : 'null',
    )
    return []
  },
  /**
   * Get today's events.
   */
  async getTodayEvents(): Promise<CalendarEvent[]> {
    const now = new Date()
    const startOfToday = new Date(
      now.getFullYear(),
      now.getMonth(),
      now.getDate(),
    )
    const endOfToday = new Date(startOfToday.getTime() + 24 * 60 * 60 * 1000)
    return this.getEvents(
      startOfToday.toISOString(),
      endOfToday.toISOString(),
      20,
    )
  },
  /**
   * Get events for the current month (for calendar widget).
   */
  async getMonthEvents(
    year?: number,
    month?: number,
  ): Promise<CalendarEvent[]> {
    const now = new Date()
    const y = year ?? now.getFullYear()
    const m = month ?? now.getMonth()
    const start = new Date(y, m, 1)
    const end = new Date(y, m + 1, 0, 23, 59, 59)
    return this.getEvents(start.toISOString(), end.toISOString(), 100)
  },
  /**
   * Get upcoming events (next N events from now).
   */
  async getUpcoming(count: number = 10): Promise<CalendarEvent[]> {
    return this.getEvents(new Date().toISOString(), undefined, count)
  },
  /**
   * Get the user's calendar list (all calendars).
   */
  async getCalendarList(): Promise<any[]> {
    if (!isMcpConnected())
      throw new Error(
        'Google no está conectado. Conectá tu cuenta desde Conectores.',
      )
    const result = await mcpCallOrThrow<any>('calendar_list_calendars')
    if (Array.isArray(result)) return result
    if (result?.items && Array.isArray(result.items)) return result.items
    if (result?.calendars && Array.isArray(result.calendars))
      return result.calendars
    if (result?.data && Array.isArray(result.data)) return result.data
    console.warn(
      '[GoogleCalendar] Unexpected calendar list shape, keys:',
      result ? Object.keys(result) : 'null',
    )
    return []
  },
}
